import React, { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { inr, getApiErrorMessage } from "@/lib/utils_pharma";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertCircle, Plus, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const ALLOWED_SPLIT_MODES = ["Cash", "UPI", "Card"];

export default function ChangePaymentModeDialog({ open, onOpenChange, bill, onSuccess }) {
  const [selectedMode, setSelectedMode] = useState("Cash");
  const [splitRows, setSplitRows] = useState([
    { mode: "Cash", amount: "" },
    { mode: "UPI", amount: "" },
  ]);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const grandTotal = Number(bill?.grand_total || 0);
  const grandTotalPaise = Math.round(grandTotal * 100);

  useEffect(() => {
    if (open && bill) {
      setReason("");
      setFormError("");
      const currentMode = bill.payment_mode === "Split" ? "Split" : (bill.payment_mode || "Cash");
      setSelectedMode(currentMode);

      if (bill.payment_mode === "Split" && Array.isArray(bill.payments) && bill.payments.length >= 2) {
        setSplitRows(bill.payments.map((p) => ({ mode: p.mode, amount: String(p.amount) })));
      } else {
        const half = Math.floor(grandTotal / 2);
        const rem = roundMoney(grandTotal - half);
        setSplitRows([
          { mode: "Cash", amount: half > 0 ? String(half) : "" },
          { mode: "UPI", amount: rem > 0 ? String(rem) : "" },
        ]);
      }
    }
  }, [open, bill, grandTotal]);

  const splitTotalPaidPaise = useMemo(() => {
    if (selectedMode !== "Split") return 0;
    return splitRows.reduce((acc, r) => acc + Math.round((Number(r.amount) || 0) * 100), 0);
  }, [selectedMode, splitRows]);

  const splitRemainingPaise = grandTotalPaise - splitTotalPaidPaise;
  const isSplitBalanced = splitTotalPaidPaise === grandTotalPaise && grandTotalPaise > 0;

  const handleModeChange = (val) => {
    setSelectedMode(val);
    setFormError("");
  };

  const handleSplitRowChange = (index, field, value) => {
    setSplitRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAddSplitRow = () => {
    if (splitRows.length >= 3) return;
    const existingModes = splitRows.map((r) => r.mode);
    const availableMode = ALLOWED_SPLIT_MODES.find((m) => !existingModes.includes(m)) || "Card";
    const rem = splitRemainingPaise > 0 ? (splitRemainingPaise / 100).toFixed(2) : "";
    setSplitRows([...splitRows, { mode: availableMode, amount: rem }]);
  };

  const handleRemoveSplitRow = (index) => {
    if (splitRows.length <= 2) return;
    setSplitRows(splitRows.filter((_, i) => i !== index));
  };

  const handleAutoFillRemaining = (index) => {
    const otherPaise = splitRows.reduce((acc, r, i) => {
      if (i === index) return acc;
      return acc + Math.round((Number(r.amount) || 0) * 100);
    }, 0);
    const neededPaise = Math.max(0, grandTotalPaise - otherPaise);
    handleSplitRowChange(index, "amount", (neededPaise / 100).toFixed(2));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setFormError("");

    if (!reason.trim()) {
      setFormError("Please enter a reason for this payment mode correction.");
      return;
    }

    if (selectedMode === "Split") {
      if (splitRows.length < 2) {
        setFormError("Split payment requires at least 2 payment methods.");
        return;
      }
      if (splitRows.length > 3) {
        setFormError("Split payment supports a maximum of 3 payment methods.");
        return;
      }
      const modes = splitRows.map((r) => r.mode);
      if (new Set(modes).size !== modes.length) {
        setFormError("Duplicate payment methods are not allowed.");
        return;
      }
      for (const r of splitRows) {
        const amt = Number(r.amount);
        if (!amt || amt <= 0) {
          setFormError(`Amount for ${r.mode} must be greater than ₹0.00.`);
          return;
        }
      }
      if (!isSplitBalanced) {
        const diff = Math.abs(splitRemainingPaise / 100).toFixed(2);
        if (splitRemainingPaise > 0) {
          setFormError(`Split payments total is short by ₹${diff}. Must equal ₹${grandTotal.toFixed(2)}.`);
        } else {
          setFormError(`Split payments total exceeds invoice total by ₹${diff}. Must equal ₹${grandTotal.toFixed(2)}.`);
        }
        return;
      }
    }

    const payload = {
      payment_mode: selectedMode,
      payments:
        selectedMode === "Split"
          ? splitRows.map((r) => ({
              mode: r.mode,
              amount: Math.round(Number(r.amount) * 100) / 100,
            }))
          : null,
      reason: reason.trim(),
    };

    setSaving(true);
    try {
      const { data } = await api.patch(`/bills/${bill.id}/payment-mode`, payload);
      toast.success(`Payment mode updated to ${selectedMode} for Bill #${bill.bill_no}`);
      if (onSuccess) {
        onSuccess(data.bill, data.audit);
      }
      onOpenChange(false);
    } catch (err) {
      const msg = getApiErrorMessage(err, "Failed to update payment mode");
      setFormError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!bill) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="change-payment-mode-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Change Payment Mode</span>
            <span className="font-mono text-xs text-muted-foreground">Bill #{bill.bill_no}</span>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          {/* Bill summary banner */}
          <div className="rounded-md border border-border bg-muted/40 p-3 text-xs space-y-1.5">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Customer:</span>
              <span className="font-medium">{bill.customer_name || "Walk-in Customer"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Current Payment Mode:</span>
              <span className="font-semibold">{bill.payment_mode}</span>
            </div>
            <div className="flex justify-between border-t border-border pt-1 font-semibold text-sm">
              <span>Invoice Grand Total:</span>
              <span className="text-primary tabular-nums">{inr(grandTotal)}</span>
            </div>
          </div>

          {/* New Payment Mode Selector */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">New Payment Mode</Label>
            <Select value={selectedMode} onValueChange={handleModeChange}>
              <SelectTrigger data-testid="new-payment-mode-trigger">
                <SelectValue placeholder="Select payment mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Cash">Cash</SelectItem>
                <SelectItem value="UPI">UPI</SelectItem>
                <SelectItem value="Card">Card</SelectItem>
                <SelectItem value="Split">Split Payment</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Split Mode Sub-form */}
          {selectedMode === "Split" && (
            <div className="space-y-3 rounded-md border border-border bg-muted/20 p-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold">Split Breakdown</span>
                {splitRows.length < 3 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs px-2"
                    onClick={handleAddSplitRow}
                  >
                    <Plus size={12} className="mr-1" /> Add Method
                  </Button>
                )}
              </div>

              <div className="space-y-2">
                {splitRows.map((row, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <div className="w-28">
                      <Select
                        value={row.mode}
                        onValueChange={(val) => handleSplitRowChange(idx, "mode", val)}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ALLOWED_SPLIT_MODES.map((m) => (
                            <SelectItem key={m} value={m}>
                              {m}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex-1">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        className="h-8 text-xs font-mono"
                        value={row.amount}
                        onChange={(e) => handleSplitRowChange(idx, "amount", e.target.value)}
                        data-testid={`split-amount-${idx}`}
                      />
                    </div>

                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 text-[11px] px-2 text-muted-foreground hover:text-foreground"
                      onClick={() => handleAutoFillRemaining(idx)}
                      title="Fill remainder"
                    >
                      Fill
                    </Button>

                    {splitRows.length > 2 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-destructive"
                        onClick={() => handleRemoveSplitRow(idx)}
                      >
                        <Trash2 size={13} />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {/* Split Balance Indicator */}
              <div className="flex items-center justify-between text-xs border-t border-border pt-2 tabular-nums">
                <span className="text-muted-foreground">
                  Entered: <strong className="text-foreground">{inr(splitTotalPaidPaise / 100)}</strong>
                </span>
                {isSplitBalanced ? (
                  <span className="text-emerald-600 flex items-center gap-1 font-semibold text-[11px]">
                    <CheckCircle2 size={13} /> Balanced
                  </span>
                ) : (
                  <span className={splitRemainingPaise > 0 ? "text-amber-600 font-semibold" : "text-destructive font-semibold"}>
                    {splitRemainingPaise > 0
                      ? `Remaining: ${inr(splitRemainingPaise / 100)}`
                      : `Over by: ${inr(Math.abs(splitRemainingPaise) / 100)}`}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Reason Field (REQUIRED) */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">
              Reason for Correction <span className="text-destructive">*</span>
            </Label>
            <Textarea
              placeholder="e.g. Customer requested UPI payment instead of Cash after billing"
              rows={2}
              className="text-xs"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="payment-correction-reason"
              required
            />
          </div>

          {/* Error Message */}
          {formError && (
            <div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-2.5 text-xs text-destructive">
              <AlertCircle size={14} className="shrink-0" />
              <span>{formError}</span>
            </div>
          )}

          <DialogFooter className="gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={saving || (selectedMode === "Split" && !isSplitBalanced) || !reason.trim()}
              data-testid="save-payment-correction"
            >
              {saving ? "Saving..." : "Update Payment"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function roundMoney(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}
